import React from 'react';
import { Badge, Button, Form } from 'react-bootstrap';

import { OverlayTemplateFolder } from '@/lomavisSharedUi/types/overlayTemplates';

export type OverlayTemplateEditorMode = 'create' | 'edit' | 'duplicate';

export interface LomavisOverlayTemplateEditorShellLabels {
  title: string;
  description: string;
  templateName: string;
  folder: string;
  noFolder: string;
  cancel: string;
  save: string;
  saving: string;
  editorRegion: string;
  editorPlaceholder: string;
  templateUuid: string;
  configuration: string;
  unassignedUuid: string;
}

export interface LomavisOverlayTemplateEditorShellProps {
  mode: OverlayTemplateEditorMode;
  templateName: string;
  folderId: string | null;
  folders: OverlayTemplateFolder[];
  scopeLabel: string;
  templateUuid?: string | null;
  configuration: Record<string, unknown>;
  statusLabel?: string;
  labels: LomavisOverlayTemplateEditorShellLabels;
  templateNameError?: string;
  configurationError?: string;
  isSaving?: boolean;
  canSave?: boolean;
  integratedEditor?: boolean;
  children?: React.ReactNode;
  onTemplateNameChange: (name: string) => void;
  onFolderChange: (folderId: string | null) => void;
  onCancel: () => void;
  onSave: () => void;
}

/**
 * Template: application shell around the separately delivered overlay editor.
 */
export const LomavisOverlayTemplateEditorShell: React.FC<
  LomavisOverlayTemplateEditorShellProps
> = ({
  mode,
  templateName,
  folderId,
  folders,
  scopeLabel,
  templateUuid,
  configuration,
  statusLabel,
  labels,
  templateNameError,
  configurationError,
  isSaving = false,
  canSave = true,
  integratedEditor = false,
  children,
  onTemplateNameChange,
  onFolderChange,
  onCancel,
  onSave
}) =>
  integratedEditor && children ? (
    <section
      className="container-fluid bg-light p-0 d-flex h-100 flex-column overflow-hidden"
      data-editor-mode={mode}
      style={{ minHeight: 0 }}
    >
      {configurationError && (
        <div className="alert alert-danger mb-0" role="alert">
          {configurationError}
        </div>
      )}

      <div
        className="flex-grow-1 overflow-hidden"
        aria-label={labels.editorRegion}
        style={{ minHeight: 0 }}
      >
        {children}
      </div>
    </section>
  ) : (
    <section
      className="container-fluid bg-light py-5 px-4 px-lg-5 min-vh-100"
      data-editor-mode={mode}
    >
      <div className="d-flex flex-column gap-5">
        <header className="d-flex flex-column flex-lg-row align-items-lg-start justify-content-between gap-4">
          <div>
            <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
              <h1 className="fs-2 fw-bold text-dark mb-0">{labels.title}</h1>
              <Badge bg="light" text="primary" className="border">
                {scopeLabel}
              </Badge>
              {statusLabel && (
                <Badge bg="light" text="secondary" className="border">
                  {statusLabel}
                </Badge>
              )}
            </div>
            <p className="text-muted mb-0">{labels.description}</p>
          </div>

          <div className="d-flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="white"
              disabled={isSaving}
              onClick={onCancel}
            >
              {labels.cancel}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="primary"
              disabled={
                isSaving || !canSave || templateName.trim().length === 0
              }
              onClick={onSave}
            >
              {isSaving && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                />
              )}
              {isSaving ? labels.saving : labels.save}
            </Button>
          </div>
        </header>

        <div className="row g-5 align-items-start">
          <div className="col-12 col-lg-4 col-xl-3">
            <div className="card border shadow-sm bg-white">
              <div className="card-body d-flex flex-column gap-4 p-4">
                <Form.Group controlId="overlay-template-name">
                  <Form.Label>{labels.templateName}</Form.Label>
                  <Form.Control
                    value={templateName}
                    isInvalid={Boolean(templateNameError)}
                    aria-invalid={Boolean(templateNameError)}
                    disabled={isSaving}
                    onChange={(event) =>
                      onTemplateNameChange(event.target.value)
                    }
                  />
                  {templateNameError && (
                    <Form.Control.Feedback type="invalid">
                      {templateNameError}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group controlId="overlay-template-folder">
                  <Form.Label>{labels.folder}</Form.Label>
                  <Form.Select
                    value={folderId ?? ''}
                    disabled={isSaving}
                    onChange={(event) =>
                      onFolderChange(event.target.value || null)
                    }
                  >
                    <option value="">{labels.noFolder}</option>
                    {folders.map((folder) => (
                      <option key={folder.id} value={folder.id}>
                        {folder.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-8 col-xl-9">
            <div
              className="card border shadow-sm bg-white overflow-hidden"
              aria-label={labels.editorRegion}
            >
              {children ?? (
                <div className="card-body d-flex flex-column gap-4 p-4">
                  <div className="d-flex align-items-center gap-3">
                    <span className="symbol symbol-40px bg-light-primary rounded-circle d-flex align-items-center justify-content-center flex-shrink-0">
                      <i
                        className="fa-regular fa-pen-ruler fs-4 text-primary"
                        aria-hidden="true"
                      />
                    </span>
                    <p className="text-muted mb-0">
                      {labels.editorPlaceholder}
                    </p>
                  </div>

                  <div>
                    <div className="text-uppercase text-muted fw-bold fs-8 mb-2">
                      {labels.templateUuid}
                    </div>
                    <code className="d-block bg-light border rounded p-3 text-break">
                      {templateUuid ?? labels.unassignedUuid}
                    </code>
                  </div>

                  <div>
                    <div className="text-uppercase text-muted fw-bold fs-8 mb-2">
                      {labels.configuration}
                    </div>
                    <pre className="bg-light border rounded p-4 mb-0 text-start overflow-auto">
                      <code>{JSON.stringify(configuration, null, 2)}</code>
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
